import { TemplateService } from "@adfenix/types";
import { TemplateUiModel } from "routes/back-office/program-builder/details/campaign/meta/template/TemplateUiModel";
import { useOfficeAndHq } from "hooks/useOfficeAndHq";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { templatesForOwnerKey } from "routes/back-office/hq-settings/templates/campaign-templates/TemplatesForSubject";
import { channelToPascalCase, subjectAttributeToPascalCase } from "utils/types";
import { TemplateGroup } from "./template/TemplateGroup";
import { ProgramService } from "@adfenix/types";
import { useFormContext } from "react-hook-form";
import { getTemplatesForOwner } from "api/services/templateService";
import { H5, Stack } from "@adfenix/alexandria";
import { useTranslation } from "react-i18next";
import { useGetImageTemplates } from "api/queries/templateService";

interface Props {}

/**
 * Responsible for orchestrating fetching and selection of templates for a program.
 * Keeping track of templates is done with react-query and react-hook-form:
 *  - allTemplates - Stored here and contains all templates for a subject that're fetched when this component mounts.
 *  - availableTemplates - Stored in react-hook-form and contains the templates the user has added to the list of available ones.
 *  - selectedTemplates - Stored in react-hook-form and contains the ones selected as default for a channel
 *
 * There's also:
 *  - selectableTemplates - These are the templates in the list of all templates that are not yet selected as available ones.
 */
export const CampaignContent = ({}: Props) => {
  const { t } = useTranslation();

  const { hq } = useOfficeAndHq();
  const { watch } = useFormContext<ProgramService.ProgramModel>();
  const subjectType = watch("subjectType");

  const { data } = useQuery([templatesForOwnerKey, hq?.id], () =>
    getTemplatesForOwner("hq", hq?.id)
  );
  const { data: imageTemplates } = useGetImageTemplates(hq?.id);
  const facebookPlatformImageTemplates = imageTemplates?.filter(
    template => !template.channels.includes("displayAd")
  );

  // These values are memoized to avoid creating new arrays when this component rerenders.
  // This ensures we avoid unexpected effects in TemplateGroups.
  const allImageTemplatesMemo = useMemo(
    () => toUiModelFromImage(facebookPlatformImageTemplates),
    [facebookPlatformImageTemplates]
  );
  const allAdTemplatesMemo = useMemo(
    () => flattenTemplateResponse(data),
    [data]
  );

  return (
    <Stack spacing="m">
      <H5>{t("programBuilder.program.template.content.header")}</H5>

      <TemplateGroup
        allTemplates={allAdTemplatesMemo.filter(
          x => x.subjectType === subjectType
        )}
        title={t("programBuilder.program.template.type.ad")}
        type="ad"
      />

      <TemplateGroup
        allTemplates={allImageTemplatesMemo.filter(
          x => x.subjectType === subjectType
        )}
        title={t("programBuilder.program.template.type.image")}
        type="image"
      />
    </Stack>
  );
};

/**
 * Templates are received from the backend in an object where each key is the channel and the value is a list of templates.
 * @param data the response from the backend when requesting templates for a subject.
 * @returns A flattened array with all templates, with each template object also having a channel property.
 */
const flattenTemplateResponse = (
  data: TemplateService.ContentTemplateModels | undefined
): TemplateUiModel[] => {
  if (!data) {
    return [];
  }
  return Object.entries(data).flatMap(([channel, templates]) => {
    return toUiModelFromContent(
      channel as keyof TemplateService.ContentTemplateModels,
      templates
    );
  });
};

const toUiModelFromContent = (
  channel: "facebook" | "twitter" | "instagram",
  templates: TemplateService.FacebookContentTemplateModel[]
): TemplateUiModel[] => {
  return templates.map(x => {
    return {
      type: "ad",
      ...x,
      subjectType: subjectAttributeToPascalCase(x.subjectType),
      channels:
        channel === "facebook"
          ? ["Facebook", "Twitter", "Instagram"]
          : [channelToPascalCase(channel)]
    };
  });
};

const toUiModelFromImage = (
  templates: TemplateService.ImageTemplateModel[] | undefined
): TemplateUiModel[] => {
  if (!templates) {
    return [];
  }
  return templates.map(x => {
    const { channels, subject, availableByDefault, ...rest } = x;
    return {
      type: "image",
      channels: channels.map(channelToPascalCase),
      subjectType: subjectAttributeToPascalCase(subject),
      default: availableByDefault,
      ...rest
    };
  });
};
